/**
 * Mail settings: signatures. Several, formatted (bold, links, colours, your logo as a picture), one default for new
 * emails and one for replies and forwards; the composer lets you switch per email.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { api, del, post } from "../../lib/api";
import { RichEditor } from "../RichEditor";
import { ErrorNote } from "../ui";

interface Sig { id: string; name: string; html: string; defaultNew: boolean; defaultReply: boolean }

export function Signatures() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["mail-signatures"], queryFn: () => api<{ items: Sig[] }>("/api/mail/signatures") });
  const items = q.data?.items ?? [];
  const [sel, setSel] = useState<string | null>(null);
  const cur = items.find((s) => s.id === (sel ?? items[0]?.id)) ?? null;
  const [draft, setDraft] = useState<{ id: string; name: string; html: string } | null>(null);
  const editing = draft && cur && draft.id === cur.id ? draft : cur ? { id: cur.id, name: cur.name, html: cur.html } : null;
  const refresh = () => void qc.invalidateQueries({ queryKey: ["mail-signatures"] });
  const save = useMutation({ mutationFn: (b: Partial<Sig> & { id: string }) => api(`/api/mail/signatures/${b.id}`, { method: "PATCH", json: { name: b.name, html: b.html, defaultNew: b.defaultNew, defaultReply: b.defaultReply } }), onSuccess: () => { setDraft(null); refresh(); } });
  const add = useMutation({ mutationFn: () => post<{ id: string }>("/api/mail/signatures", { name: items.length ? `Signature ${items.length + 1}` : "Default", html: "" }), onSuccess: (r) => { setSel(r.id); refresh(); } });
  const remove = useMutation({ mutationFn: (id: string) => del(`/api/mail/signatures/${id}`), onSuccess: () => { setSel(null); refresh(); } });
  const dirty = !!draft && !!cur && (draft.name !== cur.name || draft.html !== cur.html);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {items.map((s) => (
          <button key={s.id} type="button" onClick={() => { setSel(s.id); setDraft(null); }} className={`chip cursor-pointer text-xs ${cur?.id === s.id ? "!border-brand-600 !bg-brand-50 text-brand-700" : ""}`}>
            {s.name}{s.defaultNew ? " · new" : ""}{s.defaultReply ? " · replies" : ""}
          </button>
        ))}
        <button type="button" className="btn btn-sm" disabled={add.isPending} onClick={() => add.mutate()}><Plus size={13} /> New signature</button>
      </div>
      {editing && cur && (
        <div className="space-y-2">
          <input className="input !py-1 text-sm" value={editing.name} maxLength={60} onChange={(e) => setDraft({ ...editing, name: e.target.value })} aria-label="Signature name" />
          <RichEditor key={cur.id} value={cur.html} onChange={(h) => setDraft({ ...editing, html: h })} minHeight={110} placeholder="Your name, title, phone — add your logo with the picture button" />
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <label className="flex items-center gap-1"><input type="checkbox" checked={cur.defaultNew} onChange={(e) => save.mutate({ id: cur.id, defaultNew: e.target.checked })} /> Default for new emails</label>
            <label className="flex items-center gap-1"><input type="checkbox" checked={cur.defaultReply} onChange={(e) => save.mutate({ id: cur.id, defaultReply: e.target.checked })} /> Default for replies and forwards</label>
            <span className="flex-1" />
            <button type="button" className="btn btn-sm" disabled={remove.isPending} onClick={() => { if (confirm(`Delete the signature "${cur.name}"?`)) remove.mutate(cur.id); }}><Trash2 size={13} /> Delete</button>
            <button type="button" className="btn btn-primary btn-sm" disabled={!dirty || save.isPending} onClick={() => save.mutate({ id: cur.id, name: editing.name.trim() || cur.name, html: editing.html })}>{save.isPending ? "Saving…" : dirty ? "Save signature" : "Saved"}</button>
          </div>
        </div>
      )}
      {!items.length && q.isSuccess && <p className="muted text-xs">No signature yet. Add one — it's added under your emails, and you can switch it per email.</p>}
      <ErrorNote error={save.error ?? add.error ?? remove.error} />
    </div>
  );
}
